import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import type { ComponentProps } from "react";
import { createContext, useContext } from "react";

import { cn } from "helpers/cn";

const radioGroupVariants = cva("relative text-body-small text-subtle", {
  variants: {
    variant: {
      default: "inline-flex flex-wrap items-start",
      segmented: "inline-flex rounded-xl border border-border bg-muted p-px",
      solid: "inline-flex",
      outline: "inline-flex"
    },
    block: {
      true: "flex w-full"
    }
  },
  defaultVariants: { variant: "default" }
});

const radioGroupItemVariants = cva(
  "outline-none focus-visible:z-2 focus-visible:ring-3 focus-visible:ring-[#d4e6c3] focus-visible:ring-offset-1 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default:
          "flex size-4 shrink-0 items-center justify-center rounded-full border border-border bg-transparent transition-colors data-[state=checked]:border-brand data-[state=checked]:bg-brand enabled:group-hover:border-brand",
        segmented:
          "inline-block h-8.5 rounded-xl px-3.75 text-center leading-8 whitespace-nowrap transition-colors data-[state=checked]:bg-brand data-[state=checked]:text-white data-[state=unchecked]:enabled:hover:text-brand",
        solid:
          "relative -me-px inline-block border border-border text-center whitespace-nowrap transition-colors first-of-type:rounded-l-md last-of-type:me-0 last-of-type:rounded-r-md data-[state=checked]:z-1 data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=checked]:text-white data-[state=unchecked]:enabled:hover:text-brand disabled:bg-black/4 disabled:text-black/25 data-[state=checked]:disabled:border-border data-[state=checked]:disabled:bg-black/15 data-[state=checked]:disabled:text-black/25",
        outline:
          "relative -me-px inline-block border border-border text-center transition-colors first-of-type:rounded-l-xl last-of-type:me-0 last-of-type:rounded-r-xl data-[state=checked]:z-1 data-[state=checked]:border-brand data-[state=checked]:text-brand data-[state=unchecked]:enabled:hover:text-brand"
      },
      size: {
        default: "",
        small: ""
      },
      block: {
        true: "flex-1"
      }
    },
    compoundVariants: [
      {
        variant: ["solid", "outline"],
        size: "default",
        class: "h-8.5 px-3.75 leading-8"
      },
      { variant: ["solid", "outline"], size: "small", class: "h-6 px-1.75 leading-5.5" }
    ],
    defaultVariants: { variant: "default", size: "default" }
  }
);

type RadioGroupStyle = VariantProps<typeof radioGroupItemVariants>;

const RadioGroupContext = createContext<RadioGroupStyle>({});

type RadioGroupProps = ComponentProps<typeof RadioGroupPrimitive.Root> &
  VariantProps<typeof radioGroupVariants> &
  Pick<RadioGroupStyle, "size">;

const RadioGroup = ({ className, variant, size, block, ...props }: RadioGroupProps) => (
  <RadioGroupContext.Provider value={{ variant, size, block }}>
    <RadioGroupPrimitive.Root
      className={cn(radioGroupVariants({ variant, block }), className)}
      data-slot="radio-group"
      {...props}
    />
  </RadioGroupContext.Provider>
);

type RadioGroupItemProps = ComponentProps<typeof RadioGroupPrimitive.Item>;

const RadioGroupItem = ({ className, children, ...props }: RadioGroupItemProps) => {
  const { variant, size, block } = useContext(RadioGroupContext);
  const itemClassName = radioGroupItemVariants({ variant, size, block });

  if (variant && variant !== "default") {
    return (
      <RadioGroupPrimitive.Item
        className={cn(itemClassName, className)}
        data-slot="radio-group-item"
        {...props}
      >
        {children}
      </RadioGroupPrimitive.Item>
    );
  }

  return (
    <label
      className={cn(
        "group me-2 inline-flex last:me-0 cursor-pointer items-center leading-5.5 has-disabled:cursor-not-allowed",
        className
      )}
      data-slot="radio-group-label"
    >
      <RadioGroupPrimitive.Item
        className={itemClassName}
        data-slot="radio-group-item"
        {...props}
      >
        <RadioGroupPrimitive.Indicator
          className="size-1.5 rounded-full bg-white"
          data-slot="radio-group-indicator"
        />
      </RadioGroupPrimitive.Item>
      {children != null && <span className="px-2">{children}</span>}
    </label>
  );
};

export { RadioGroup, RadioGroupItem };
